/**
 * CompletarPerfil — página exibida após login via Google quando o usuário
 * ainda não tem CPF cadastrado.
 *
 * Fluxo:
 *  1. Login Google → backend retorna `needs_profile_completion: true`
 *  2. AuthModal redireciona para `/completar-perfil`
 *  3. Usuário informa CPF → `PATCH /api/v2/users/me`
 *  4. Sucesso → navega para `/` (ou intent salvo)
 *
 * O CPF aqui é obrigatório (alinhado com a política do cadastro tradicional).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { IdCard, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatCpf, validateCpf, stripCpf } from '../utils/cpf';

export default function CompletarPerfil() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [cpf, setCpf] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Se já tem CPF ou não está autenticado, sai daqui.
  // `has_cpf` é setado pelo backend quando o CPF está armazenado em qualquer
  // forma (campo legado `cpf` OU encriptado em `cpf_encrypted`/`cpf_hash`).
  // Sem esse check, quem completou o perfil via OAuth fica preso nesse modal
  // toda vez que loga.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }
    if (user?.cpf || user?.has_cpf) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateCpf(cpf)) {
      setError('CPF inválido. Verifique os dígitos.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.patch('/v2/users/me', { cpf: stripCpf(cpf) });
      toast.success('Perfil completo! Bem-vindo(a).');
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Não foi possível salvar o CPF.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50 px-4 py-12"
      data-testid="completar-perfil-page"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
            <IdCard className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Complete seu perfil
          </h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Para resgatar cupons, avaliar negócios e usar todas as funções,
            precisamos confirmar seu CPF.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="cpf" className="block text-sm font-medium text-gray-700 mb-1">
              CPF <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="cpf"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={(e) => { setCpf(formatCpf(e.target.value)); if (error) setError(''); }}
                inputMode="numeric"
                maxLength={14}
                className={`pl-10 ${error ? 'border-red-400' : ''}`}
                data-testid="completar-cpf-input"
                autoFocus
              />
            </div>
            {error && <p className="text-xs text-red-500 mt-1" data-testid="completar-cpf-error">{error}</p>}
          </div>

          <Button
            type="submit"
            className="w-full bg-purple-600 hover:bg-purple-700 h-11"
            disabled={submitting || !cpf}
            data-testid="completar-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
            ) : (
              <><CheckCircle2 className="w-4 h-4 mr-2" /> Concluir cadastro</>
            )}
          </Button>
        </form>

        <p className="text-xs text-gray-400 text-center mt-4">
          Seu CPF é armazenado de forma segura e usado apenas para validar resgates e prevenir fraude.
        </p>
      </div>
    </div>
  );
}
